import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  AlertTriangle,
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  LifeBuoy,
  Plus,
} from 'lucide-react';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Drawer } from '@/shared/components/ui/Drawer';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import {
  useServiceDeskLookups,
  useServiceDeskUser,
  useKnowledgeArticles,
  useServiceDeskMutations,
  useServiceDeskSummary,
  useServiceTicket,
  useServiceTickets,
} from '../api';
import type {
  ServiceTicket,
  SourceChannel,
  TicketCategory,
  TicketFilters,
  TicketKind,
  TicketPriority,
  TicketStatus,
} from '../types';
import {
  canCreateClientSupport,
  canManageKnowledge,
  formatDateTime,
  formatRemainingMinutes,
  humanize,
  isItOperator,
  personName,
  priorityTone,
  statusTone,
  ticketActionAvailability,
} from '../viewModel';
import { ServiceDeskSummaryCards } from '../components/ServiceDeskSummaryCards';
import { ComplaintsTab } from '../components/ComplaintsTab';

/* ────────────────────────────── shared bits ─────────────────────────────── */

function slaTone(state: ServiceTicket['sla']['state']): BadgeTone {
  if (state === 'breached') return 'danger';
  if (state === 'warning') return 'warning';
  if (state === 'healthy' || state === 'met') return 'success';
  return 'neutral';
}

const textareaClass =
  'w-full rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40';

const KIND_OPTIONS = [
  { label: 'Internal (IT)', value: 'internal' },
  { label: 'Client support', value: 'client_support' },
];
const STATUS_OPTIONS: { label: string; value: TicketStatus }[] = (
  ['open', 'triaged', 'assigned', 'in_progress', 'waiting_requester', 'resolved', 'closed'] as TicketStatus[]
).map((s) => ({ label: humanize(s), value: s }));
const PRIORITY_OPTIONS: { label: string; value: TicketPriority }[] = (
  ['normal', 'high', 'critical'] as TicketPriority[]
).map((p) => ({ label: humanize(p), value: p }));
const CHANNEL_OPTIONS: { label: string; value: SourceChannel }[] = (
  ['web', 'mobile', 'whatsapp', 'email', 'portal', 'system'] as SourceChannel[]
).map((c) => ({ label: humanize(c), value: c }));
const TIER_OPTIONS = [
  { label: 'Ad hoc', value: 'ad_hoc' },
  { label: 'Silver', value: 'silver' },
  { label: 'Gold', value: 'gold' },
];
const CATEGORY_OPTIONS: { label: string; value: TicketCategory }[] = (
  ['hardware', 'software', 'access', 'network', 'billing', 'service', 'other'] as TicketCategory[]
).map((c) => ({ label: humanize(c), value: c }));

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as { response?: { data?: { error?: string } } };
    if (maybe.response?.data?.error) return maybe.response.data.error;
  }
  return fallback;
}

/* ───────────────────────────── Service Desk ─────────────────────────────── */

export function ServiceDeskPage() {
  const user = useServiceDeskUser();
  const [tab, setTab] = useState<'tickets' | 'complaints' | 'knowledge'>('tickets');
  const [filters, setFilters] = useState<TicketFilters>({ kind: '', status: '', priority: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: summary } = useServiceDeskSummary();
  const { data: ticketList, isLoading, isError } = useServiceTickets(filters);
  const tickets = ticketList?.data ?? [];

  return (
    <div className="flex h-full flex-col gap-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <LifeBuoy className="h-6 w-6 text-primary" aria-hidden />
          <div>
            <h1 className="text-display-sm font-semibold text-on-surface">Service Desk</h1>
            <p className="text-body-sm text-on-surface-variant">
              SLA-driven internal IT and client support ticketing
            </p>
          </div>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          New Ticket
        </Button>
      </div>

      <ServiceDeskSummaryCards summary={summary} />

      <div role="tablist" aria-label="Service Desk sections" className="flex gap-1 border-b border-outline">
        {(
          [
            { key: 'tickets', label: 'Tickets', icon: LifeBuoy },
            { key: 'complaints', label: 'Complaints', icon: AlertTriangle },
            { key: 'knowledge', label: 'Knowledge Base', icon: BookOpen },
          ] as const
        ).filter((t) => t.key !== 'complaints' || isItOperator(user)).map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 rounded-t-md px-4 py-2 text-body-sm font-medium transition-colors ${
              tab === t.key
                ? 'border-b-2 border-primary text-primary'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <t.icon className="h-4 w-4" aria-hidden />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'tickets' ? (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44">
              <Select
                label="Queue"
                name="filter-kind"
                options={KIND_OPTIONS}
                placeholder="All queues"
                value={filters.kind ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value as TicketKind | '' }))}
              />
            </div>
            <div className="w-44">
              <Select
                label="Status"
                name="filter-status"
                options={STATUS_OPTIONS}
                placeholder="All statuses"
                value={filters.status ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value as TicketStatus | '' }))}
              />
            </div>
            <div className="w-44">
              <Select
                label="Priority"
                name="filter-priority"
                options={PRIORITY_OPTIONS}
                placeholder="All priorities"
                value={filters.priority ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value as TicketPriority | '' }))}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Spinner />
            </div>
          ) : isError ? (
            <EmptyState title="Failed to load tickets" description="Check your connection or try refreshing." />
          ) : tickets.length === 0 ? (
            <EmptyState
              title="No tickets in this view"
              description="Adjust the filters or raise a new ticket."
              action={<Button onClick={() => setCreateOpen(true)}>New Ticket</Button>}
            />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-outline">
              <table className="w-full min-w-[820px] text-left text-body-sm">
                <caption className="sr-only">Service tickets</caption>
                <thead className="bg-surface-variant text-on-surface-variant">
                  <tr>
                    <th scope="col" className="px-3 py-2 font-medium">Ticket</th>
                    <th scope="col" className="px-3 py-2 font-medium">Title</th>
                    <th scope="col" className="px-3 py-2 font-medium">Queue</th>
                    <th scope="col" className="px-3 py-2 font-medium">Priority</th>
                    <th scope="col" className="px-3 py-2 font-medium">Status</th>
                    <th scope="col" className="px-3 py-2 font-medium">SLA</th>
                    <th scope="col" className="px-3 py-2 font-medium">Assignee</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className="cursor-pointer border-t border-outline bg-surface hover:bg-surface-variant/60"
                      onClick={() => setSelectedId(ticket.id)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedId(ticket.id);
                        }
                      }}
                    >
                      <td className="px-3 py-2 font-mono text-data-md text-on-surface">{ticket.ticketNumber}</td>
                      <td className="max-w-[280px] truncate px-3 py-2 text-on-surface">{ticket.title}</td>
                      <td className="px-3 py-2">
                        <Badge tone={ticket.kind === 'client_support' ? 'info' : 'neutral'}>
                          {ticket.kind === 'client_support' ? 'Client' : 'Internal'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Badge tone={priorityTone(ticket.priority)}>{humanize(ticket.priority)}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Badge tone={statusTone(ticket.status)}>{humanize(ticket.status)}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-2">
                          <Badge tone={slaTone(ticket.sla.state)}>{humanize(ticket.sla.state)}</Badge>
                          <span className="text-on-surface-variant">
                            {formatRemainingMinutes(ticket.sla.remainingMinutes)}
                          </span>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-on-surface-variant">{personName(ticket.assignedTo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : tab === 'complaints' ? (
        <ComplaintsTab />
      ) : (
        <KnowledgeTab canManage={canManageKnowledge(user)} />
      )}

      <CreateTicketModal
        open={createOpen}
        allowClientSupport={canCreateClientSupport(user)}
        onClose={() => setCreateOpen(false)}
      />

      <TicketDrawer ticketId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

/* ─────────────────────────── create ticket modal ────────────────────────── */

function CreateTicketModal({
  open,
  allowClientSupport,
  onClose,
}: {
  open: boolean;
  allowClientSupport: boolean;
  onClose: () => void;
}) {
  const { createTicket } = useServiceDeskMutations();
  const [kind, setKind] = useState<TicketKind>('internal');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sourceChannel, setSourceChannel] = useState<SourceChannel>('web');
  const [contractTier, setContractTier] = useState('ad_hoc');
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');
  const { data: lookups } = useServiceDeskLookups(allowClientSupport && open);

  const kindOptions = allowClientSupport ? KIND_OPTIONS : KIND_OPTIONS.filter((k) => k.value === 'internal');

  const submit = () => {
    if (!title.trim() || !description.trim()) {
      toast.error('Title and description are required.');
      return;
    }
    createTicket.mutate(
      {
        kind,
        title: title.trim(),
        description: description.trim(),
        sourceChannel,
        ...(kind === 'client_support' ? { contractTier: contractTier as never } : {}),
        ...(clientId ? { clientId } : {}),
        ...(projectId ? { projectId } : {}),
      },
      {
        onSuccess: (ticket) => {
          toast.success(`Ticket ${ticket.ticketNumber} created.`);
          setTitle('');
          setDescription('');
          setClientId('');
          setProjectId('');
          onClose();
        },
        onError: (err) => toast.error(errorMessage(err, 'Failed to create ticket.')),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Ticket"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={createTicket.isPending || !title.trim() || !description.trim()}>
            {createTicket.isPending ? 'Creating…' : 'Create Ticket'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Select
          label="Queue"
          name="new-kind"
          options={kindOptions}
          value={kind}
          onChange={(e) => setKind(e.target.value as TicketKind)}
        />
        <Input label="Title" name="new-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <div>
          <label htmlFor="new-description" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Description
          </label>
          <textarea
            id="new-description"
            rows={4}
            className={textareaClass}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <Select
          label="Source channel"
          name="new-channel"
          options={CHANNEL_OPTIONS}
          value={sourceChannel}
          onChange={(e) => setSourceChannel(e.target.value as SourceChannel)}
        />
        {kind === 'client_support' && (
          <>
            <Select
              label="Client"
              name="new-client"
              options={[{ label: 'No client linked', value: '' }, ...(lookups?.clients ?? []).map((c) => ({ label: c.company ? `${c.company} (${c.name})` : c.name, value: c.id }))]}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
            <Select
              label="Project"
              name="new-project"
              options={[{ label: 'No project linked', value: '' }, ...(lookups?.projects ?? []).map((p) => ({ label: `${p.name} (${p.key})`, value: p.id }))]}
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            />
            <Select
              label="Contract tier"
              name="new-tier"
              options={TIER_OPTIONS}
              value={contractTier}
              onChange={(e) => setContractTier(e.target.value)}
            />
          </>
        )}
        <p className="text-body-sm text-on-surface-variant">
          Priority and category are classified automatically from the description; SLA deadlines are
          assigned from the contract tier.
        </p>
      </div>
    </Modal>
  );
}

/* ────────────────────────────── ticket drawer ───────────────────────────── */

function TicketDrawer({ ticketId, onClose }: { ticketId: string | null; onClose: () => void }) {
  const user = useServiceDeskUser();
  const { data: ticket, isLoading } = useServiceTicket(ticketId);
  const mutations = useServiceDeskMutations();
  const { data: users = [] } = useUserOptions(ticketId);

  const [assignee, setAssignee] = useState('');
  const [assignTeam, setAssignTeam] = useState('it');
  const [replyNote, setReplyNote] = useState('');
  const [replyChannel, setReplyChannel] = useState<SourceChannel>('web');
  const [resolutionSummary, setResolutionSummary] = useState('');
  const [knowledgeDraft, setKnowledgeDraft] = useState(false);
  const [csat, setCsat] = useState('5');

  const actions = useMemo(() => ticketActionAvailability(user, ticket), [user, ticket]);

  const userOptions = users.map((u) => ({ label: u.display_name, value: u.id }));

  const run = <T,>(
    mutation: { mutate: (vars: T, opts: { onSuccess: () => void; onError: (err: unknown) => void }) => void },
    vars: T,
    successMessage: string,
    after?: () => void,
  ) =>
    mutation.mutate(vars, {
      onSuccess: () => {
        toast.success(successMessage);
        after?.();
      },
      onError: (err: unknown) => toast.error(errorMessage(err, 'Action failed.')),
    });

  return (
    <Drawer
      open={!!ticketId}
      onClose={onClose}
      width="w-[520px]"
      title={ticket ? `${ticket.ticketNumber} — ${ticket.title}` : 'Ticket'}
    >
      {isLoading || !ticket ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-wrap gap-2">
            <Badge tone={ticket.kind === 'client_support' ? 'info' : 'neutral'}>
              {ticket.kind === 'client_support' ? 'Client support' : 'Internal'}
            </Badge>
            <Badge tone={priorityTone(ticket.priority)}>{humanize(ticket.priority)}</Badge>
            <Badge tone={statusTone(ticket.status)}>{humanize(ticket.status)}</Badge>
            <Badge tone={slaTone(ticket.sla.state)}>SLA {humanize(ticket.sla.state)}</Badge>
            {ticket.escalationLevel > 0 && <Badge tone="warning">Escalation L{ticket.escalationLevel}</Badge>}
          </div>

          <p className="whitespace-pre-wrap text-body-sm text-on-surface">{ticket.description}</p>

          <Card>
            <CardHeader title="Details" />
            <CardBody className="grid grid-cols-2 gap-x-4 gap-y-2 text-body-sm">
              <span className="text-on-surface-variant">Requester</span>
              <span className="text-on-surface">{personName(ticket.requesterUserId, '—')}</span>
              <span className="text-on-surface-variant">Assignee</span>
              <span className="text-on-surface">{personName(ticket.assignedTo)}</span>
              <span className="text-on-surface-variant">Team</span>
              <span className="text-on-surface">{humanize(ticket.assignedTeam)}</span>
              <span className="text-on-surface-variant">Category</span>
              <span className="text-on-surface">{humanize(ticket.category)}</span>
              <span className="text-on-surface-variant">Channel</span>
              <span className="text-on-surface">{humanize(ticket.sourceChannel)}</span>
              <span className="text-on-surface-variant">Contract tier</span>
              <span className="text-on-surface">{humanize(ticket.contractTier)}</span>
              <span className="text-on-surface-variant">Created</span>
              <span className="text-on-surface">{formatDateTime(ticket.createdAt)}</span>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="SLA clock" />
            <CardBody className="grid grid-cols-2 gap-x-4 gap-y-2 text-body-sm">
              <span className="text-on-surface-variant">First response due</span>
              <span className="text-on-surface">{formatDateTime(ticket.firstResponseDueAt)}</span>
              <span className="text-on-surface-variant">First responded</span>
              <span className="text-on-surface">{formatDateTime(ticket.firstRespondedAt)}</span>
              <span className="text-on-surface-variant">Resolution due</span>
              <span className="text-on-surface">{formatDateTime(ticket.resolutionDueAt)}</span>
              <span className="text-on-surface-variant">Resolved</span>
              <span className="text-on-surface">{formatDateTime(ticket.resolvedAt)}</span>
              <span className="text-on-surface-variant">Time remaining</span>
              <span className="text-on-surface">{formatRemainingMinutes(ticket.sla.remainingMinutes)}</span>
            </CardBody>
          </Card>

          {actions.assign && (
            <Card>
              <CardHeader title="Assign" />
              <CardBody className="flex flex-col gap-3">
                <Select
                  label="Assignee"
                  name="assign-user"
                  options={userOptions}
                  placeholder="Select a user"
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                />
                <Select
                  label="Team"
                  name="assign-team"
                  options={[
                    { label: 'IT', value: 'it' },
                    { label: 'Support', value: 'support' },
                    { label: 'Development', value: 'development' },
                    { label: 'Vendor', value: 'vendor' },
                  ]}
                  value={assignTeam}
                  onChange={(e) => setAssignTeam(e.target.value)}
                />
                <Button
                  size="sm"
                  disabled={!assignee || mutations.assignTicket.isPending}
                  onClick={() =>
                    run(
                      mutations.assignTicket,
                      { id: ticket.id, assignedTo: assignee, assignedTeam: assignTeam },
                      'Ticket assigned.',
                    )
                  }
                >
                  Assign ticket
                </Button>
              </CardBody>
            </Card>
          )}

          {actions.respond && (
            <Card>
              <CardHeader title="Respond" />
              <CardBody className="flex flex-col gap-3">
                <div>
                  <label htmlFor="reply-note" className="mb-1.5 block text-body-sm font-medium text-on-surface">
                    Reply to requester
                  </label>
                  <textarea
                    id="reply-note"
                    rows={3}
                    className={textareaClass}
                    value={replyNote}
                    onChange={(e) => setReplyNote(e.target.value)}
                  />
                </div>
                <Select
                  label="Channel"
                  name="reply-channel"
                  options={CHANNEL_OPTIONS}
                  value={replyChannel}
                  onChange={(e) => setReplyChannel(e.target.value as SourceChannel)}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    disabled={!replyNote.trim() || mutations.respondTicket.isPending}
                    onClick={() =>
                      run(
                        mutations.respondTicket,
                        { id: ticket.id, note: replyNote.trim(), channel: replyChannel },
                        'Response recorded.',
                        () => setReplyNote(''),
                      )
                    }
                  >
                    Send response
                  </Button>
                  {ticket.status !== 'in_progress' && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={mutations.updateTicketStatus.isPending}
                      onClick={() =>
                        run(
                          mutations.updateTicketStatus,
                          { id: ticket.id, status: 'in_progress' as const },
                          'Ticket moved to in progress.',
                        )
                      }
                    >
                      Start work
                    </Button>
                  )}
                  {actions.waitForRequester && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={mutations.updateTicketStatus.isPending}
                      onClick={() =>
                        run(
                          mutations.updateTicketStatus,
                          { id: ticket.id, status: 'waiting_requester' as const },
                          'Waiting on requester.',
                        )
                      }
                    >
                      Wait for requester
                    </Button>
                  )}
                  {actions.escalate && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={mutations.escalateTicket.isPending}
                      onClick={() => run(mutations.escalateTicket, { id: ticket.id }, 'Ticket escalated.')}
                    >
                      <ArrowUpRight className="h-4 w-4" aria-hidden />
                      Escalate
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          {actions.resolve && (
            <Card>
              <CardHeader title="Resolve" />
              <CardBody className="flex flex-col gap-3">
                <div>
                  <label
                    htmlFor="resolution-summary"
                    className="mb-1.5 block text-body-sm font-medium text-on-surface"
                  >
                    Resolution summary
                  </label>
                  <textarea
                    id="resolution-summary"
                    rows={3}
                    className={textareaClass}
                    value={resolutionSummary}
                    onChange={(e) => setResolutionSummary(e.target.value)}
                  />
                </div>
                <label className="flex items-center gap-2 text-body-sm text-on-surface">
                  <input
                    type="checkbox"
                    checked={knowledgeDraft}
                    onChange={(e) => setKnowledgeDraft(e.target.checked)}
                  />
                  Draft a knowledge-base article from this resolution
                </label>
                <Button
                  size="sm"
                  disabled={!resolutionSummary.trim() || mutations.resolveTicket.isPending}
                  onClick={() =>
                    run(
                      mutations.resolveTicket,
                      {
                        id: ticket.id,
                        resolutionSummary: resolutionSummary.trim(),
                        createKnowledgeDraft: knowledgeDraft,
                      },
                      'Ticket resolved.',
                      () => setResolutionSummary(''),
                    )
                  }
                >
                  <CheckCircle2 className="h-4 w-4" aria-hidden />
                  Resolve ticket
                </Button>
              </CardBody>
            </Card>
          )}

          {actions.confirm && (
            <Card>
              <CardHeader title="Confirm resolution" />
              <CardBody className="flex flex-col gap-3">
                <Select
                  label="Satisfaction (CSAT)"
                  name="csat"
                  options={['5', '4', '3', '2', '1'].map((v) => ({ label: `${v} / 5`, value: v }))}
                  value={csat}
                  onChange={(e) => setCsat(e.target.value)}
                />
                <Button
                  size="sm"
                  disabled={mutations.confirmTicket.isPending}
                  onClick={() =>
                    run(
                      mutations.confirmTicket,
                      { id: ticket.id, csat: Number(csat) },
                      'Resolution confirmed — ticket closed.',
                    )
                  }
                >
                  Confirm &amp; close
                </Button>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader title="Timeline" />
            <CardBody>
              {ticket.timeline?.length ? (
                <ol className="flex flex-col gap-3">
                  {ticket.timeline.map((event, index) => (
                    <li
                      key={event._id ?? `${event.action}-${index}`}
                      className="border-l-2 border-outline pl-3 text-body-sm"
                    >
                      <p className="font-medium text-on-surface">{humanize(event.action)}</p>
                      {event.note && <p className="text-on-surface-variant">{event.note}</p>}
                      <p className="text-on-surface-variant">
                        {personName(event.actorUserId, 'System')} · {formatDateTime(event.occurredAt)}
                      </p>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-body-sm text-on-surface-variant">No events recorded yet.</p>
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </Drawer>
  );
}

/* ─────────────────────────────── knowledge ──────────────────────────────── */

function KnowledgeTab({ canManage }: { canManage: boolean }) {
  const [category, setCategory] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const { data: articles = [], isLoading } = useKnowledgeArticles(category);
  const { createKnowledge, publishKnowledge } = useServiceDeskMutations();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [newCategory, setNewCategory] = useState<TicketCategory>('service');
  const [visibility, setVisibility] = useState<'internal' | 'client'>('internal');

  const submit = () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required.');
      return;
    }
    createKnowledge.mutate(
      { title: title.trim(), body: body.trim(), category: newCategory, visibility },
      {
        onSuccess: () => {
          toast.success('Draft article created.');
          setTitle('');
          setBody('');
          setCreateOpen(false);
        },
        onError: (err) => toast.error(errorMessage(err, 'Failed to create article.')),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="w-52">
          <Select
            label="Category"
            name="kb-category"
            options={CATEGORY_OPTIONS}
            placeholder="All categories"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
        </div>
        {canManage && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            New Article
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : articles.length === 0 ? (
        <EmptyState
          icon={<BookOpen className="h-8 w-8" aria-hidden />}
          title="No knowledge articles"
          description="Resolve tickets with the knowledge-draft option, or add an article directly."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {articles.map((article) => (
            <Card key={article._id}>
              <CardHeader
                title={article.title}
                action={
                  <span className="flex items-center gap-2">
                    <Badge tone={article.status === 'published' ? 'success' : 'neutral'}>
                      {humanize(article.status)}
                    </Badge>
                    <Badge tone={article.visibility === 'client' ? 'info' : 'neutral'}>
                      {humanize(article.visibility)}
                    </Badge>
                  </span>
                }
              />
              <CardBody className="flex flex-col gap-2">
                <p className="line-clamp-4 whitespace-pre-wrap text-body-sm text-on-surface-variant">
                  {article.body}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-body-sm text-on-surface-variant">
                    {humanize(article.category)} · {formatDateTime(article.createdAt)}
                  </span>
                  {canManage && article.status === 'draft' && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={publishKnowledge.isPending}
                      onClick={() =>
                        publishKnowledge.mutate(article._id, {
                          onSuccess: () => toast.success('Article published.'),
                          onError: (err) => toast.error(errorMessage(err, 'Failed to publish article.')),
                        })
                      }
                    >
                      Publish
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Knowledge Article"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={createKnowledge.isPending}>
              {createKnowledge.isPending ? 'Saving…' : 'Create Draft'}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <Input label="Title" name="kb-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <div>
            <label htmlFor="kb-body" className="mb-1.5 block text-body-sm font-medium text-on-surface">
              Body
            </label>
            <textarea
              id="kb-body"
              rows={6}
              className={textareaClass}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <Select
            label="Category"
            name="kb-new-category"
            options={CATEGORY_OPTIONS}
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value as TicketCategory)}
          />
          <Select
            label="Visibility"
            name="kb-visibility"
            options={[
              { label: 'Internal', value: 'internal' },
              { label: 'Client-facing', value: 'client' },
            ]}
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as 'internal' | 'client')}
          />
        </div>
      </Modal>
    </div>
  );
}
